import _, { type Many } from 'lodash';
import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import DataTable from '../../../components/data-table';
import Paginate from '../../../components/paginate';
import { useHotel } from '../../../context/hotel-context';
import { useUser } from '../../../context/user-context';
import { AttendanceStatus } from '../../../features/meetings/components/attendance-status';
import {
  type GetMeetingsQuery,
  useGetMeetingsQuery,
  useRemoveMeetingMutation,
} from '../../../features/meetings/gql/_gen_/meeting.gql';
import {
  AttendanceStatus as AttendanceStatusEnum,
  type Meeting,
  UserRole,
} from '../../../graphql/types';
import { dayDiff, dow, today } from '../../../helpers/date-helpers';
import { useConfirmDialog } from '../../../hooks/use-confirm-dialog';
import {
  SortDir,
  type TableFilters,
  useDataTableOptions,
} from '../../../reducers/use-data-table-options';
import type { Actions, DataRow, FormField } from '../../../types/data-table';

interface PaginatedMeetingsObject {
  pages: DataRow[][];
  index: number;
  totalMeetings: number;
  totalPages: number;
}

interface MeetingTableRows {
  rows: DataRow[];
  startEventIndex: number;
}

function MeetingsTable() {
  const objFields = ['id', 'link', 'shareLink', 'meetingDate', 'createdById', 'attendanceStatus'];
  const { hotel } = useHotel();
  const { user } = useUser();
  const canDeleteMeetings = user?.role === UserRole.Admin || user?.role === UserRole.Manager;
  const { confirm, dialog: confirmDialog } = useConfirmDialog();

  const [options, setOptions] = useDataTableOptions({
    sort: { key: 'meetingDate', dir: SortDir.DESC },
  });
  const [dataRange, setDataRange] = useState<string[]>([]);
  const [tableData, setTableData] = useState<DataRow[]>([]);
  const [tableFilters, setTableFilters] = useState<TableFilters>({});
  const [totalPages, setTotalPages] = useState(1);
  const [paginatedMeetingsObject, setPaginatedMeetingsObject] = useState<PaginatedMeetingsObject>({
    pages: [],
    index: 0,
    totalMeetings: 0,
    totalPages: 1,
  });
  const [page, setPage] = useState<number | undefined>(undefined);
  const PAGE_SIZE = 10;

  const {
    data: dataMeetings,
    loading: loadingMeetings,
    networkStatus: networkStatusMeetings,
    refetch: refetchMeetings,
  } = useGetMeetingsQuery({
    skip: !hotel?.brand_code,
    variables: {
      brandCode: hotel?.brand_code,
    },
    onCompleted: (data) => {
      if (data?.getMeetings) {
        const minDate = _.minBy(data.getMeetings, 'meetingDate')?.meetingDate;
        const maxDate = _.maxBy(data.getMeetings, 'meetingDate')?.meetingDate;
        if (minDate && maxDate) {
          setDataRange([minDate, maxDate]);
        }
      }
    },
  });

  const [removeMeeting, { loading: deletingMeeting }] = useRemoveMeetingMutation();

  const handleDelete = async (id: string | string[]) => {
    const ids = Array.isArray(id) ? id : [id];
    try {
      await Promise.all(
        ids.map((meetingId) =>
          removeMeeting({
            variables: { meetingId },
          }),
        ),
      );
    } catch (error) {
      toast.error('The meeting could not be deleted. Please try again.');
      throw error;
    } finally {
      await refetchMeetings();
    }
  };

  const handleDeleteAll = async () => {
    if (!dataMeetings?.getMeetings) return;
    const ids = dataMeetings.getMeetings.map((m) => m?.id).filter(Boolean) as string[];
    await handleDelete(ids);
  };

  // Deleting a meeting is permanent, so confirm first.
  const confirmDelete = (ids: string[]) =>
    confirm({
      title: ids.length > 1 ? `Delete ${ids.length} meetings?` : 'Delete this meeting?',
      message:
        ids.length > 1
          ? 'These meetings and their notes will be permanently deleted. This cannot be undone.'
          : 'This meeting and its notes will be permanently deleted. This cannot be undone.',
    });

  const confirmDeleteAll = () =>
    confirm({
      title: 'Delete all meetings?',
      message: `All ${
        dataMeetings?.getMeetings?.length || 0
      } meetings shown for this property, and their notes, will be permanently deleted. This cannot be undone.`,
      confirmLabel: 'Delete All',
    });

  const actions: Actions | undefined = canDeleteMeetings
    ? {
        bulkUpsert: () => Promise.resolve(),
        confirmDelete,
        confirmDeleteAll,
        delete: handleDelete,
        deleteStatus: deletingMeeting,
        deleteAll: handleDeleteAll,
        networkStatus: networkStatusMeetings,
        update: () => Promise.resolve(),
        updateStatus: false,
      }
    : undefined;

  useEffect(() => {
    if (hotel?.brand_code) {
      refetchMeetings({
        brandCode: hotel.brand_code,
      });
    }
  }, [hotel]);

  useEffect(() => {
    if (dataMeetings) {
      const rows = getRows(dataMeetings);
      const paginatedMeetings = paginateRows(rows.rows, rows.startEventIndex, PAGE_SIZE, page);
      setTableData(paginatedMeetings.pages[paginatedMeetings.index]);
      setPaginatedMeetingsObject(paginatedMeetings);
    }
  }, [dataMeetings, loadingMeetings, options, tableFilters]);

  useEffect(() => {
    setTotalPages(paginatedMeetingsObject.totalPages);
    setPage(paginatedMeetingsObject.index);
  }, [paginatedMeetingsObject]);

  useEffect(() => {
    if (page !== undefined) {
      setTableData(paginatedMeetingsObject.pages[page]);
    }
  }, [page]);

  function pageUp() {
    if (page !== undefined) {
      if (page < totalPages - 1) {
        setPage(page + 1);
      }
    }
  }

  function pageDown() {
    if (page !== undefined) {
      if (page >= 1) {
        setPage(page - 1);
      }
    }
  }

  const filterItems = (meeting: Meeting) => {
    return filterDays(meeting, 'meetingDate') && filterString(meeting, 'notes');
  };

  const filterDays = (meeting: Meeting, attr: keyof Meeting) => {
    const filter = tableFilters[attr];
    if (filter) {
      const targetDate = meeting[attr] as string;
      const dowKey = dow(targetDate).toLowerCase() as keyof typeof filter;
      return filter[dowKey] === true;
    }
    return true;
  };

  const filterString = (meeting: Meeting, attr: keyof Meeting) => {
    const filter = tableFilters[attr];
    if (filter) {
      return String(meeting[attr]).toLowerCase().includes(String(filter).toLowerCase());
    }
    return true;
  };

  const getRows = (data: GetMeetingsQuery): MeetingTableRows => {
    const rows: DataRow[] = [];
    if (data?.getMeetings) {
      const startMeeting = {
        diff: 1000,
        index: 0,
      };
      _.orderBy(
        data.getMeetings as Meeting[],
        [
          (meeting) =>
            meeting[options.sort.key as keyof Meeting] === null
              ? ''
              : String(meeting[options.sort.key as keyof Meeting]).toLowerCase(),
        ],
        options.sort.dir as Many<boolean | 'desc' | 'asc'> | undefined,
      )
        .filter(filterItems)
        .forEach((meeting: Meeting) => {
          const diff = dayDiff(meeting.meetingDate!, today());
          if (Math.abs(diff) < startMeeting.diff) {
            startMeeting.diff = Math.abs(diff);
            startMeeting.index = rows.length;
          }
          let row = _.pick(meeting, objFields) as DataRow;
          row = {
            ...row,
            link: <a href={`/report/${meeting.id}`}>{`${meeting.id}`}</a>,
            shareLink: (
              <a href={`/shared_report/${meeting.id}`} target='_blank' rel='noreferrer'>
                Shared Link{' '}
              </a>
            ),
            attendanceStatus: (
              <AttendanceStatus
                currentStatus={meeting.attendanceStatus || AttendanceStatusEnum.Attended}
                disabled={true}
                meetingCategory={meeting.category}
              />
            ),
          };
          rows.push(row);
        });
      return {
        rows: rows,
        startEventIndex: startMeeting.index,
      };
    } else {
      return {
        rows: rows,
        startEventIndex: 0,
      };
    }
  };

  const splitRows = (rows: DataRow[], startIndex: number, pageSize: number) => {
    if (rows.length <= pageSize) {
      return [rows];
    }
    const secondHalf = rows.splice(startIndex);
    while (secondHalf.length < pageSize) {
      secondHalf.unshift(rows.pop() as DataRow);
    }
    return [rows, secondHalf];
  };

  const paginateRows = (
    rows: DataRow[],
    startIndex: number,
    pageSize: number,
    currentPage?: number,
  ): PaginatedMeetingsObject => {
    const rowGroups = splitRows(rows, startIndex, pageSize);
    if (rowGroups.length === 1) {
      return {
        pages: [rows],
        index: 0,
        totalMeetings: dataMeetings?.getMeetings?.length || 0,
        totalPages: 1,
      };
    }
    const pagesArr = [];
    while (rowGroups[0].length > 0) {
      pagesArr.unshift(rowGroups[0].splice(-pageSize));
    }
    const startPage = pagesArr.length;
    while (rowGroups[1].length > 0) {
      pagesArr.push(rowGroups[1].slice(0, pageSize));
      rowGroups[1].splice(0, pageSize);
    }
    return {
      pages: pagesArr,
      index: currentPage || startPage,
      totalMeetings: dataMeetings?.getMeetings?.length || 0,
      totalPages: pagesArr.length,
    };
  };

  const formFields = objFields.map((key) => {
    return {
      bulk: [''].includes(key),
      id: 'id',
      label: key === 'attendanceStatus' ? 'Attendance Status' : _.startCase(key),
      name: key,
      readOnly: true,
      type: key.toLowerCase().includes('date') ? 'date' : 'text',
      value: key.toLowerCase().includes('date') ? today() : '',
    };
  });

  return (
    <div className='w-full mt-2'>
      {loadingMeetings ? (
        <p>Loading...</p>
      ) : (
        <div>
          <Paginate
            first={page || 0}
            last={totalPages}
            previous={pageDown}
            next={pageUp}
            total={paginatedMeetingsObject.totalMeetings || 0}
          />
          <DataTable
            actions={actions}
            dataRange={dataRange}
            fields={formFields as FormField[]}
            name='Meeting'
            options={options}
            rows={tableData}
            setOptions={setOptions}
            setTableFilters={setTableFilters}
            tableFilters={tableFilters}
          />
        </div>
      )}
      {confirmDialog}
    </div>
  );
}

export default MeetingsTable;
